<template>
  <div
    class="header-cart-component"
    :class="{
      'product-page': isProductPage,
    }">
    <ClientOnly>
      <template #fallback>
        <a class="cart-toggle" :class="{ active: visible }">
          <span
            class="cart-badge"
            :class="{ 'cart-badge-xl': totalQuantityInCart > 99 }"
            v-text="cartStore.isLoaded ? totalQuantityInCart : null" />
          <span class="cart-icon" v-html="isWhitelabel ? CartIconWlRaw : CartIconRaw" />
        </a>
      </template>
      <UPopover
        v-model:open="visible"
        :ui="{
          base: 'dropdown-body',
          container: 'dropdown',
          popper: {
            strategy: 'absolute',
          },
        }">
        <a class="cart-toggle" :class="{ active: visible }">
          <span
            class="cart-badge"
            :class="{ 'cart-badge-xl': totalQuantityInCart > 99 }"
            v-text="cartStore.isLoaded ? totalQuantityInCart : null" />
          <span class="cart-icon" v-html="isWhitelabel ? CartIconWlRaw : CartIconRaw" />
        </a>
        <template #panel>
          <div class="cart-dropdown">
            <template v-if="totalQuantityInCart">
              <div class="mini-cart-wrap scrollbar">
                <div v-for="(item, itemIndex) in cartStore.items" :key="itemIndex">
                  <div class="mini-cart-item">
                    <div class="flex flex-row gap-4">
                      <div class="cart-image" :style="{ backgroundImage: `url('${item.media}')` }" :title="item.name" />
                      <div class="relative w-full">
                        <p class="item-manufacturer">
                          <NuxtLink :to="buildManufacturerUrl(item.manufacturer)" :title="item.manufacturer">
                            {{ item.manufacturer }}
                          </NuxtLink>
                        </p>
                        <h5 class="item-name">
                          <template v-if="item.slug != null">
                            <NuxtLink
                              :to="buildProductUrl(buildCartProductSlug(item), item.item_id)"
                              :title="item.name">
                              {{ item.name }}
                            </NuxtLink>
                          </template>
                          <template v-else>
                            {{ item.name }}
                          </template>
                        </h5>
                        <div v-if="item?.fields?.length" class="item-fields-wrap">
                          <div v-for="(field, fieldIndex) in item.fields" :key="fieldIndex" class="item-fields">
                            <span class="item-field-title">{{ field.title }}:</span>
                            <span class="item-field-value">{{ field.value }}</span>
                          </div>
                        </div>
                        <div class="absolute top-0 right-0 alt:-top-3 alt:-right-3">
                          <CartRemove :item-index="itemIndex" header />
                        </div>
                      </div>
                    </div>
                    <div class="item-spacer"></div>
                    <div class="flex gap-4">
                      <div>
                        <CartQuantity :item-index="itemIndex" />
                      </div>
                      <div class="flex flex-row gap-2 items-center justify-center ml-auto">
                        <span class="text-brand-primary text-lg font-bold font-primary whitespace-nowrap leading-none">
                          {{ toPrice(item.price) }}
                        </span>
                        <span class="text-black dark:text-white text-base font-normal whitespace-nowrap leading-none">
                          ({{ toPriceConverted(item.price) }})
                        </span>
                        <Sticker v-if="false">
                          <i class="fa-regular fa-clock"></i> <span v-html="t('cart.delivery_time', { time: '1-2' })" />
                        </Sticker>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-1">
                <span class="font-medium text-neutral1 dark:text-dmText1">{{ t('cart.summary.total_price') }}</span>
                <span
                  class="font-bold ml-auto text-2xl font-primary text-text1 dark:text-dmText1 whitespace-nowrap leading-none">
                  <span class="leading-none">{{ toPrice(total) }}</span>
                  <span class="text-black dark:text-white text-base font-normal whitespace-nowrap">
                    ({{ toPriceConverted(total) }})
                  </span>
                </span>
              </div>

              <Button class="w-full" type="button" @click.prevent="handleClick()">{{ t('header.cart') }}</Button>
            </template>
            <template v-else>
              <div class="flex items-center justify-center flex-col h-full gap-4">
                <span class="h5">{{ t('header.your_cart_is_empty') }}</span>
                <Button class="w-full" type="button" @click.prevent="handleClick()">
                  {{ t('header.choose_products') }}
                </Button>
              </div>
            </template>
          </div>
        </template>
      </UPopover>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { useStoreConfig } from '~/store/storeConfig'
import { useLang } from '~/composables/useLang'
import { useCartStore } from '~/store/cart'
import { Routes } from '~/enums/Routes'
import Button from '~/components/Button.vue'
import { useDropdownStore } from '~/store/dropdownStore'
import CartIconRaw from '@/assets/icons/icon-cart.svg?raw'
import CartIconWlRaw from '@/assets/icons/wl-icon-cart.svg?raw'

import CartRemove from '~/components/Cart/CartRemove.vue'
import CartQuantity from '~/components/Cart/CartQuantity.vue'
import Sticker from '~/components/Product/Sticker.vue'
import toPrice from '~/utils/toPrice'
import toPriceConverted from '~/utils/toPriceConverted'
import buildProductUrl from '~/utils/routing/buildProductUrl'
import buildManufacturerUrl from '~/utils/routing/buildManufacturerUrl'
import type { NavigationFailure, RouteLocationRaw } from 'vue-router'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import buildCartProductSlug from '~/utils/cart/buildCartProductSlug'
import lockScroll from '~/utils/lockScroll'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import { PagesPrefixEnum } from '~/enums/PagesPrefixEnum'

const { t } = useLang()
const cartStore = useCartStore()
const { isMobile } = useStoreConfig()
const { isPerkuJavTheme, isWhitelabel, isTarpininke } = useTheme()
const visible = ref<boolean>(false)
const route = useRoute()
const isProductPage = computed(() => route.name === `${PagesPrefixEnum}${Routes.ProductSlug}`)
const totalQuantityInCart = computed(() => cartStore.totalQuantity)
const total = computed(() => cartStore.totalSum)

function onUpdate(value: boolean): void {
  visible.value = value
}

function handleClick(): Promise<void | NavigationFailure> | RouteLocationRaw {
  if (isPerkuJavTheme || isTarpininke) {
    return navigateTo(cartStore.totalItems > 0 ? getLocalizedRoute(Routes.Checkout.Cart) : buildCategoryUrl(null))
  }

  if (isWhitelabel) {
    return navigateTo(getLocalizedRoute(cartStore.totalItems > 0 ? Routes.Checkout.Cart : Routes.Marketplace), {
      redirectCode: 301,
    })
  }

  return navigateTo(getLocalizedRoute(cartStore.totalItems > 0 ? Routes.Checkout.Cart : Routes.Market), {
    redirectCode: 301,
  })
}

const dropdownStore = useDropdownStore()

function clickEventListener(event: MouseEvent | PointerEvent) {
  const element = document.querySelector('.header-cart-component')
  if (event.target instanceof Element) {
    if (
      element &&
      (!element.contains(event.target) ||
        event.target instanceof HTMLAnchorElement ||
        event.target instanceof HTMLButtonElement)
    ) {
      visible.value = false
    }
  }
}

watch(visible, (newValue) => {
  if (isMobile) {
    lockScroll(newValue)
  }
  const header = document.getElementById('mobile-header-jav')

  if (newValue) {
    emit('closeMenu')
    emit('closeSearch')

    dropdownStore.open('header_cart')
    window?.addEventListener('click', clickEventListener)
    if (header) {
      header.style.zIndex = 1100
    }
  } else {
    if (header) {
      header.style.zIndex = 33
    }
    window?.removeEventListener('click', clickEventListener)
  }
})

watch(dropdownStore.dropdowns, (newValue) => {
  visible.value = newValue.header_cart
})

if (!cartStore.isLoaded) {
  await cartStore.fetch()
}

const emit = defineEmits(['closeMenu', 'closeSearch'])
</script>

<style lang="postcss" scoped>
.header-cart-component {
  @apply flex gap-4;

  :deep(.cart-toggle) {
    @apply flex w-10 h-10 relative rounded-full;

    &.active {
      @apply bg-brand-primaryLight;

      .cart-icon {
        svg {
          path {
            @apply fill-brand-primary;
          }
        }
      }
    }
  }

  .cart-icon {
    @apply flex w-10 h-10 items-center justify-center;
  }

  .cart-dropdown {
    @apply flex flex-col gap-4 h-full;
  }
}

.dark .header-cart-component {
  :deep(.cart-toggle) {
    .cart-icon {
      svg {
        path {
          @apply fill-white;
        }
      }
    }
    &:hover,
    &.active {
      @apply bg-dmGray5;

      .cart-icon {
        svg {
          path {
            @apply fill-orange;
          }
        }
      }
    }
  }

  :deep(.dropdown) {
    .dropdown-body {
      background: theme('colors.dmGray1');
    }
  }
}

.header-cart-component {
  :deep(.dropdown) {
    width: 23.375rem;
    max-width: 100vw;
    max-height: calc(100% - 50px);
    height: 100%;

    @apply max-w-full md:max-w-[24.375rem];

    .arrow {
      display: none;
    }

    > div {
      @apply h-full;
    }

    .dropdown-body {
      background: theme('colors.white');
      box-shadow: 0 4px 10px rgba(205, 205, 205, 0.5);
      border-radius: 0;
      border: 0;
      padding: 1.25rem;

      @apply h-full md:h-min md:rounded-md md:alt:rounded-sm;
    }

    @media not all and (min-width: calc(theme('screens.md') - 1px)) {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      width: 100%;
      transform: none !important;
      top: theme('vars.mobileHeaderHeight') !important;
    }
  }

  &.product-page {
    :deep(.dropdown) {
      max-height: calc(100% - 50px - 67px);
    }
  }

  .cart-badge {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 1rem;
    height: 1rem;
    background-color: theme('colors.brand.primary');
    border-radius: 0.5rem;
    font-weight: 700;
    font-size: 10px;
    line-height: 1rem;
    text-align: center;
    letter-spacing: 0.025em;
    color: theme('colors.white');

    &.cart-badge-xl {
      width: 1.5rem;
    }
  }

  .cart-image {
    @apply flex w-20 h-20 min-w-20 min-h-20 bg-contain bg-no-repeat bg-center dark:bg-white dark:rounded-sm;
  }

  .item-manufacturer {
    @apply flex items-center text-sm font-medium py-1 text-text2 dark:text-dmText2;

    a:hover {
      @apply text-brand-primary;
    }
  }

  .item-name {
    @apply flex items-center text-base font-normal py-1 text-black dark:text-white font-secondary;

    a:hover {
      @apply text-brand-primary;
    }
  }

  .item-fields-wrap {
    @apply flex gap-0 flex-col lg:flex-row flex-wrap;
    .item-fields {
      @apply flex items-center py-1 pr-4;
      .item-field-title {
        @apply text-xs font-normal text-text2 dark:text-dmText2;
      }

      .item-field-value {
        @apply text-sm font-medium pl-[0.3125rem] text-black dark:text-dmText1;
      }
    }
  }

  .mini-cart-wrap {
    @apply flex flex-col gap-4 h-full overflow-x-auto sm:min-h-[12rem] sm:max-h-[calc(60vh-7.5rem)];
    margin-right: -4px;
  }

  .mini-cart-wrap::-webkit-scrollbar {
    width: 16px;
  }

  .mini-cart-wrap::-webkit-scrollbar-thumb {
    border: 4px solid transparent;
    background-clip: padding-box;
  }

  .mini-cart-wrap::-webkit-scrollbar-track {
    background: transparent;
  }

  .mini-cart-item {
    @apply p-4 border border-neutral6 dark:border-dmGray4;
    border-radius: theme('vars.borderRadiusLight');
    margin-right: 4px;
  }

  .item-spacer {
    @apply border-t border-neutral6 dark:border-dmGray1 my-4;
  }
}
</style>

<style lang="postcss">
.alt {
  .cart-toggle {
    @apply !rounded-sm;
  }

  .cart-icon {
    svg {
      path {
        @apply fill-white;
      }
    }
  }

  .cart-badge {
    background-color: theme('colors.red.DEFAULT') !important;
  }
}

.wl {
  .header-cart-component {
    @apply mr-4;

    .cart-dropdown {
      @apply text-text1;
    }
  }

  .cart-toggle {
    @apply flex w-10 h-10 relative rounded-full;

    .cart-icon {
      svg {
        path {
          @apply fill-brand-primary;
        }
      }
    }

    &.active {
      @apply bg-brand-primaryLight;

      .cart-icon {
        svg {
          path {
            @apply fill-black;
          }
        }
      }
    }
  }
  :deep(.dropdown-wrapper) {
    .cart-toggle {
      &.active {
        @apply bg-brand-primaryLight;

        .cart-icon {
          svg {
            path {
              @apply !fill-black;
            }
          }
        }
      }
    }
  }
}

.tp {
  .header-cart-component {
    .dropdown-wrapper {
      .cart-toggle {
        &.active {
          .cart-icon {
            svg {
              path {
                @apply !fill-brand-black;
              }
            }
          }
        }
      }
    }
  }
}
</style>
